import { useEffect, useMemo, useState, type MouseEvent as ReactMouseEvent } from 'react';
import ReactFlow, {
  Background,
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  Position,
  type Edge,
  type Node,
  type ReactFlowInstance
} from 'reactflow';
import 'reactflow/dist/style.css';
import ELK from 'elkjs/lib/elk.bundled.js';

import type { TopologyV2Graphschema } from '@app/types/sdk';

type CloudRow = { label: string; value: string };
type CloudLayout = { container: boolean; lane: string; rank: number; order: number };
type CloudNode = {
  id: string;
  title: string;
  subtitle: string;
  role: string;
  parentId: string;
  rows: CloudRow[];
  layout: CloudLayout;
};
type CloudEdge = { id: string; fromId: string; toId: string; role: string; relation?: string };

interface CloudConnectivityViewProps {
  graph: TopologyV2Graphschema;
  selectedNodeIds: Set<string>;
  selectedEdgeIds: Set<string>;
  onNodeSelect: (event: ReactMouseEvent, nodeId: string) => void;
  onEdgeSelect: (event: ReactMouseEvent, edgeId: string) => void;
  onPaneClick: (event: ReactMouseEvent) => void;
}

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : undefined;

const text = (value: unknown): string =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? String(value).trim() : '';

const rowsFrom = (value: unknown): CloudRow[] =>
  (Array.isArray(value) ? value : []).flatMap((item) => {
    const row = asRecord(item);
    const label = text(row?.label);
    const rowValue = text(row?.value);
    return label && rowValue ? [{ label, value: rowValue }] : [];
  });

const cloudLayoutFrom = (value: unknown): CloudLayout => {
  const layout = asRecord(value);
  return {
    container: layout?.container === true,
    lane: text(layout?.lane) || 'network',
    rank: Number.isFinite(Number(layout?.rank)) ? Number(layout?.rank) : 3,
    order: Number.isFinite(Number(layout?.order)) ? Number(layout?.order) : 0
  };
};

const parseCloudGraph = (graph: TopologyV2Graphschema): { nodes: CloudNode[]; edges: CloudEdge[] } => {
  const nodes = (graph.nodes ?? []).flatMap((node) => {
    const properties = asRecord(node.properties);
    const presentation = asRecord(properties?.presentation);
    const cloud = asRecord(presentation?.cloud);
    if (!presentation || !cloud) {
      return [];
    }
    const id = text(node.id);
    if (!id) {
      return [];
    }
    return [{
      id,
      title: text(presentation.title) || text(node.displayName) || id,
      subtitle: text(presentation.subtitle),
      role: text(cloud.nodeRole) || 'resource',
      parentId: text(cloud.parentId),
      rows: rowsFrom(presentation.rows),
      layout: cloudLayoutFrom(cloud.layout)
    }];
  });
  const edges = (graph.edges ?? []).flatMap((edge) => {
    const properties = asRecord(edge.properties);
    const cloud = asRecord(asRecord(properties?.presentation)?.cloud);
    const labels = asRecord(edge.labels);
    const id = text(edge.id);
    const fromId = text(edge.fromId);
    const toId = text(edge.toId);
    if (!id || !fromId || !toId || !cloud) {
      return [];
    }
    return [{ id, fromId, toId, role: text(cloud.edgeRole) || 'connectivity', relation: text(labels?.relation) }];
  });
  return { nodes, edges };
};

const roleLabel = (role: string): string => role.replaceAll('_', ' ') || 'Cloud resource';

const colorForRole = (role: string): string => {
  if (role === 'network' || role === 'availability_zone' || role === 'subnet') return '#2563eb';
  if (role === 'transit_hub' || role === 'transit_attachment' || role === 'vpn_connection') return '#7c3aed';
  if (role === 'internet_gateway' || role === 'nat_gateway' || role === 'public_ip') return '#d97706';
  return '#334155';
};

const elk = new ELK();
const CLOUD_NODE_WIDTH = 218;
const CLOUD_NODE_HEIGHT = 88;
const CLOUD_HANDLE_SLOTS = 20;

type CloudLayoutInput = {
  nodes: CloudNode[];
  edges: CloudEdge[];
  layoutEdges: Array<{ source: string; target: string }>;
  externalAttachmentIds: Set<string>;
};

const cloudEdgeAppearance = (role: string) => {
  if (role === 'peering') return { color: '#7c3aed', dash: '8 5', width: 2.75 };
  if (role === 'attachment') return { color: '#0f766e', dash: undefined, width: 3 };
  if (role === 'hierarchy') return { color: '#2563eb', dash: undefined, width: 2.75 };
  if (role === 'membership') return { color: '#0f766e', dash: '6 4', width: 2.5 };
  if (role === 'dependency') return { color: '#d97706', dash: undefined, width: 2.5 };
  return { color: '#475569', dash: undefined, width: 2.5 };
};

export const cloudEdgeHandles = (
  source: { x: number; y: number } | undefined,
  target: { x: number; y: number } | undefined
): { sourceHandle: string; targetHandle: string } => {
  if (!source || !target) return { sourceHandle: 'source-bottom', targetHandle: 'target-top' };
  const deltaX = target.x - source.x;
  const deltaY = target.y - source.y;
  if (Math.abs(deltaX) > Math.abs(deltaY)) {
    return deltaX >= 0
      ? { sourceHandle: 'source-right', targetHandle: 'target-left' }
      : { sourceHandle: 'source-left', targetHandle: 'target-right' };
  }
  return deltaY >= 0
    ? { sourceHandle: 'source-bottom', targetHandle: 'target-top' }
    : { sourceHandle: 'source-top', targetHandle: 'target-bottom' };
};

export const cloudRelationshipHandles = (
  edge: CloudEdge,
  nodeById: Map<string, CloudNode>,
  externalAttachmentIds: Set<string>,
  positionsByNodeId: Map<string, { x: number; y: number }>
): { sourceHandle: string; targetHandle: string } => {
  const source = nodeById.get(edge.fromId);
  const target = nodeById.get(edge.toId);
  if (source?.role === 'transit_hub' && target?.role === 'transit_attachment') {
    return externalAttachmentIds.has(target.id)
      ? { sourceHandle: 'source-top', targetHandle: 'target-bottom' }
      : { sourceHandle: 'source-bottom', targetHandle: 'target-top' };
  }
  if (source?.role === 'transit_attachment' && target?.role === 'vpn_connection') {
    return { sourceHandle: 'source-top', targetHandle: 'target-bottom' };
  }
  if (source?.role === 'transit_attachment' && target?.role === 'network') {
    return { sourceHandle: 'source-bottom', targetHandle: 'target-top' };
  }
  // Parent/child and attachment relations are vertical even when ELK spreads
  // siblings widely across the canvas. This keeps every hierarchy line on the
  // head/bottom of its resource card instead of making it appear to stop at a
  // neighbouring side wall.
  if (edge.role === 'hierarchy' || edge.role === 'attachment') {
    const sourcePosition = positionsByNodeId.get(edge.fromId);
    const targetPosition = positionsByNodeId.get(edge.toId);
    return !sourcePosition || !targetPosition || targetPosition.y >= sourcePosition.y
      ? { sourceHandle: 'source-bottom', targetHandle: 'target-top' }
      : { sourceHandle: 'source-top', targetHandle: 'target-bottom' };
  }
  return cloudEdgeHandles(positionsByNodeId.get(edge.fromId), positionsByNodeId.get(edge.toId));
};

const usesCentralVerticalPorts = (edge: CloudEdge): boolean =>
  edge.role === 'hierarchy' || edge.role === 'attachment';

export const cloudEdgePortAssignments = (
  edges: CloudEdge[],
  positionsByNodeId: Map<string, { x: number; y: number }>,
  nodeById: Map<string, CloudNode>,
  externalAttachmentIds: Set<string>
): Map<string, { sourceHandle: string; targetHandle: string }> => {
  const connections = edges.map((edge) => ({
    edge,
    handles: cloudRelationshipHandles(edge, nodeById, externalAttachmentIds, positionsByNodeId)
  }));
  const indices = new Map<string, number>();
  const grouped = new Map<string, typeof connections>();
  for (const connection of connections) {
    const sourceKey = `${connection.edge.fromId}:${connection.handles.sourceHandle}`;
    const targetKey = `${connection.edge.toId}:${connection.handles.targetHandle}`;
    grouped.set(sourceKey, [...(grouped.get(sourceKey) ?? []), connection]);
    grouped.set(targetKey, [...(grouped.get(targetKey) ?? []), connection]);
  }
  for (const group of grouped.values()) {
    [...group]
      .sort((left, right) => left.edge.id.localeCompare(right.edge.id))
      .forEach((connection, index) => {
        const sourceKey = `${connection.edge.id}:source`;
        const targetKey = `${connection.edge.id}:target`;
        if (!indices.has(sourceKey)) indices.set(sourceKey, index % CLOUD_HANDLE_SLOTS);
        if (!indices.has(targetKey)) indices.set(targetKey, index % CLOUD_HANDLE_SLOTS);
      });
  }
  return new Map(connections.map(({ edge, handles }) => {
    // Structural cloud relations deliberately share the midpoint of the
    // appropriate boundary. This is the visual contract for the hierarchy:
    // parent bottom -> child top (and the inverse for the VPN branch).
    if (usesCentralVerticalPorts(edge)) return [edge.id, handles];
    return [edge.id, {
      sourceHandle: `${handles.sourceHandle}-${indices.get(`${edge.id}:source`) ?? 0}`,
      targetHandle: `${handles.targetHandle}-${indices.get(`${edge.id}:target`) ?? 0}`
    }];
  }));
};

export const buildCloudConnectivityLayoutInput = (cloudNodes: CloudNode[], cloudEdges: CloudEdge[]): CloudLayoutInput => {
  // The backend no longer derives a region card because the site already owns
  // the account-and-region scope. Ignore one defensively if an older response
  // or cached snapshot still contains it; availability-zone nodes are normal
  // cloud resources and are deliberately not filtered here.
  const candidateNodes = cloudNodes.filter((node) => node.role !== 'region');
  const candidateNodeById = new Map(candidateNodes.map((node) => [node.id, node]));
  const candidateEdges = cloudEdges.filter((edge) => candidateNodeById.has(edge.fromId) && candidateNodeById.has(edge.toId));
  // A local TGW attachment is useful only when the response includes the
  // attachment relation to a locally rendered VPC or VPN. Peering attachments
  // have only a TGW hierarchy edge in this one-region response; their remote
  // endpoint belongs to intersite topology, so omit that incomplete branch.
  const connectedAttachmentIds = new Set(
    candidateEdges
      .filter((edge) => edge.role === 'attachment')
      .filter((edge) => candidateNodeById.get(edge.fromId)?.role === 'transit_attachment')
      .map((edge) => edge.fromId)
  );
  const nodes = candidateNodes.filter((node) => node.role !== 'transit_attachment' || connectedAttachmentIds.has(node.id));
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const edges = candidateEdges.filter((edge) => nodeById.has(edge.fromId) && nodeById.has(edge.toId));
  const externalAttachmentIds = new Set<string>();
  for (const edge of edges) {
    if (edge.role === 'attachment' && nodeById.get(edge.fromId)?.role === 'transit_attachment' && nodeById.get(edge.toId)?.role === 'vpn_connection') {
      externalAttachmentIds.add(edge.fromId);
    }
  }
  const layoutEdges = new Map<string, { source: string; target: string }>();
  const addLayoutEdge = (source: string, target: string) => {
    if (source === target || !nodeById.has(source) || !nodeById.has(target)) return;
    layoutEdges.set(`${source}->${target}`, { source, target });
  };

  // Presentation parentage controls placement only. Semantic edges keep their
  // original direction and relation, so member_of and depends_on do not need
  // to be rewritten as contains just to produce a readable hierarchy.
  for (const node of nodes) {
    if (!node.parentId) continue;
    const parent = nodeById.get(node.parentId);
    if (!parent) continue;
    if (node.role === 'transit_attachment' && parent.role === 'transit_hub' && externalAttachmentIds.has(node.id)) {
      addLayoutEdge(node.id, parent.id);
      continue;
    }
    addLayoutEdge(parent.id, node.id);
  }

  for (const edge of edges) {
    // Peering or other future connectivity edges remain visible, but do not
    // manufacture parent/child placement. Only graph hierarchy and attachment
    // relations determine the tree geometry.
    if (edge.role !== 'hierarchy' && edge.role !== 'attachment') continue;
    const source = nodeById.get(edge.fromId);
    const target = nodeById.get(edge.toId);
    if (!source || !target) continue;

    if (edge.role === 'hierarchy' && source.role === 'transit_hub' && externalAttachmentIds.has(target.id)) {
      // The graph correctly records TGW -> attachment. In the cloud view, a
      // VPN and TGW-peering attachments are external branches, so place them
      // above their TGW instead of mixing them with VPC attachments below.
      addLayoutEdge(target.id, source.id);
      continue;
    }
    if (edge.role === 'attachment' && externalAttachmentIds.has(source.id) && target.role === 'vpn_connection') {
      addLayoutEdge(target.id, source.id);
      continue;
    }
    addLayoutEdge(source.id, target.id);
  }

  return { nodes, edges, layoutEdges: [...layoutEdges.values()], externalAttachmentIds };
};

const positionDetachedVpcSubtreesAtSide = (
  positions: Map<string, { x: number; y: number }>,
  input: CloudLayoutInput
): Map<string, { x: number; y: number }> => {
  const nodeById = new Map(input.nodes.map((node) => [node.id, node]));
  const attachedVpcIds = new Set(
    input.edges
      .filter((edge) => edge.role === 'attachment')
      .filter((edge) => nodeById.get(edge.fromId)?.role === 'transit_attachment')
      .filter((edge) => nodeById.get(edge.toId)?.role === 'network')
      .map((edge) => edge.toId)
  );
  const detachedVpcIds = input.nodes
    .filter((node) => node.role === 'network' && !attachedVpcIds.has(node.id))
    .map((node) => node.id)
    .sort();
  const attachedVpcY = input.nodes
    .filter((node) => attachedVpcIds.has(node.id))
    .map((node) => positions.get(node.id)?.y)
    .filter((value): value is number => value !== undefined)
    .sort((left, right) => left - right);
  if (detachedVpcIds.length === 0 || attachedVpcY.length === 0) return positions;

  const childIdsByParentId = new Map<string, string[]>();
  const addChild = (parentId: string, childId: string) => {
    const children = childIdsByParentId.get(parentId) ?? [];
    if (!children.includes(childId)) childIdsByParentId.set(parentId, [...children, childId]);
  };
  for (const node of input.nodes) {
    if (node.parentId) addChild(node.parentId, node.id);
  }
  for (const edge of input.edges.filter((edge) => edge.role === 'hierarchy')) {
    addChild(edge.fromId, edge.toId);
  }
  const subtreeIdsByVpcId = new Map<string, Set<string>>();
  for (const vpcId of detachedVpcIds) {
    const subtreeIds = new Set([vpcId]);
    const pending = [vpcId];
    while (pending.length > 0) {
      const currentId = pending.pop()!;
      for (const childId of childIdsByParentId.get(currentId) ?? []) {
        if (!subtreeIds.has(childId)) {
          subtreeIds.add(childId);
          pending.push(childId);
        }
      }
    }
    subtreeIdsByVpcId.set(vpcId, subtreeIds);
  }

  const detachedNodeIds = new Set([...subtreeIdsByVpcId.values()].flatMap((ids) => [...ids]));
  const mainGraphRight = Math.max(
    ...[...positions.entries()]
      .filter(([id]) => !detachedNodeIds.has(id))
      .map(([, position]) => position.x + CLOUD_NODE_WIDTH),
    0
  );
  const targetVpcY = attachedVpcY[Math.floor(attachedVpcY.length / 2)];
  const moved = new Map(positions);
  let nextX = mainGraphRight + 240;
  for (const vpcId of detachedVpcIds) {
    const rootPosition = positions.get(vpcId);
    const subtreeIds = subtreeIdsByVpcId.get(vpcId);
    if (!rootPosition || !subtreeIds) continue;
    const subtreePositions = [...subtreeIds]
      .map((id) => positions.get(id))
      .filter((position): position is { x: number; y: number } => position !== undefined);
    const left = Math.min(...subtreePositions.map((position) => position.x));
    const right = Math.max(...subtreePositions.map((position) => position.x + CLOUD_NODE_WIDTH));
    const deltaX = nextX - left;
    const deltaY = targetVpcY - rootPosition.y;
    for (const id of subtreeIds) {
      const position = positions.get(id);
      if (position) moved.set(id, { x: position.x + deltaX, y: position.y + deltaY });
    }
    nextX += right - left + 180;
  }
  return moved;
};

const fallbackCloudLayout = (nodes: CloudNode[]): Node[] =>
  [...nodes]
    .sort((left, right) => left.layout.rank - right.layout.rank || left.layout.order - right.layout.order || left.title.localeCompare(right.title))
    .map((node, index) => ({
      id: node.id,
      type: 'cloud',
      position: { x: (index % 5) * (CLOUD_NODE_WIDTH + 48), y: Math.floor(index / 5) * (CLOUD_NODE_HEIGHT + 64) },
      data: { cloudNode: node },
      style: { width: CLOUD_NODE_WIDTH, minHeight: CLOUD_NODE_HEIGHT },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top
    }));

const layoutCloudNodes = async (input: CloudLayoutInput): Promise<Node[]> => {
  const sortedNodes = [...input.nodes].sort(
    (left, right) => left.layout.order - right.layout.order || left.title.localeCompare(right.title)
  );
  try {
    const layout = await elk.layout({
      id: 'cloud-connectivity',
      layoutOptions: {
        'elk.algorithm': 'layered',
        'elk.direction': 'DOWN',
        'elk.layered.spacing.nodeNodeBetweenLayers': '112',
        'elk.spacing.nodeNode': '44',
        'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
        'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP'
      },
      children: sortedNodes.map((node) => ({ id: node.id, width: CLOUD_NODE_WIDTH, height: CLOUD_NODE_HEIGHT })),
      edges: input.layoutEdges.map((edge, index) => ({ id: `layout-${index}`, sources: [edge.source], targets: [edge.target] }))
    } as any);
    const elkPositions = new Map((layout.children ?? []).map((child: any) => [String(child.id), { x: Number(child.x ?? 0), y: Number(child.y ?? 0) }]));
    const positions = positionDetachedVpcSubtreesAtSide(elkPositions, input);
    return sortedNodes.map((node) => ({
      id: node.id,
      type: 'cloud',
      position: positions.get(node.id) ?? { x: 0, y: 0 },
      data: { cloudNode: node },
      style: { width: CLOUD_NODE_WIDTH, minHeight: CLOUD_NODE_HEIGHT },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top
    }));
  } catch {
    return fallbackCloudLayout(sortedNodes);
  }
};

const CloudNodeCard = ({ data }: { data: { cloudNode: CloudNode } }) => {
  const node = data.cloudNode;
  const handles = ['top', 'right', 'bottom', 'left'] as const;
  const hiddenHandle = (side: typeof handles[number], index: number) => {
    const position = `${((index + 1) / (CLOUD_HANDLE_SLOTS + 1)) * 100}%`;
    return {
      opacity: 0,
      width: 1,
      height: 1,
      border: 0,
      background: 'transparent',
      pointerEvents: 'none' as const,
      ...(side === 'top' || side === 'bottom' ? { left: position } : { top: position })
    };
  };
  const hiddenCentreHandle = (side: 'top' | 'bottom') => ({
    ...hiddenHandle(side, 0),
    left: '50%'
  });
  const positionForSide = { top: Position.Top, right: Position.Right, bottom: Position.Bottom, left: Position.Left } as const;
  return <div className={`cloud-connectivity__node ${node.layout.container ? 'cloud-connectivity__node--container' : ''}`}>
    <Handle id="target-top" type="target" position={Position.Top} style={hiddenCentreHandle('top')} />
    <Handle id="source-top" type="source" position={Position.Top} style={hiddenCentreHandle('top')} />
    <Handle id="target-bottom" type="target" position={Position.Bottom} style={hiddenCentreHandle('bottom')} />
    <Handle id="source-bottom" type="source" position={Position.Bottom} style={hiddenCentreHandle('bottom')} />
    {handles.flatMap((side) => Array.from({ length: CLOUD_HANDLE_SLOTS }, (_, index) => [
      <Handle key={`target-${side}-${index}`} id={`target-${side}-${index}`} type="target" position={positionForSide[side]} style={hiddenHandle(side, index)} />,
      <Handle key={`source-${side}-${index}`} id={`source-${side}-${index}`} type="source" position={positionForSide[side]} style={hiddenHandle(side, index)} />
    ])).flat()}
    <span className="cloud-connectivity__node-role" style={{ color: colorForRole(node.role) }}>{roleLabel(node.role)}</span>
    <strong>{node.title}</strong>
    {node.subtitle ? <small>{node.subtitle}</small> : null}
  </div>;
};

export const CloudConnectivityView = ({ graph, selectedNodeIds, selectedEdgeIds, onNodeSelect, onEdgeSelect, onPaneClick }: CloudConnectivityViewProps) => {
  const parsed = useMemo(() => parseCloudGraph(graph), [graph]);
  const layoutInput = useMemo(() => buildCloudConnectivityLayoutInput(parsed.nodes, parsed.edges), [parsed.edges, parsed.nodes]);
  const [instance, setInstance] = useState<ReactFlowInstance | null>(null);
  const [layoutNodes, setLayoutNodes] = useState<Node[]>([]);
  useEffect(() => {
    let cancelled = false;
    void layoutCloudNodes(layoutInput).then((nextNodes) => {
      if (!cancelled) setLayoutNodes(nextNodes);
    });
    return () => {
      cancelled = true;
    };
  }, [layoutInput]);

  const nodes = useMemo(
    () => layoutNodes.map((node) => ({ ...node, selected: selectedNodeIds.has(node.id) })),
    [layoutNodes, selectedNodeIds]
  );
  const positionsByNodeId = useMemo(
    () => new Map(layoutNodes.map((node) => [node.id, node.position])),
    [layoutNodes]
  );
  const cloudNodesById = useMemo(
    () => new Map(layoutInput.nodes.map((node) => [node.id, node])),
    [layoutInput.nodes]
  );
  const edgePortsById = useMemo(
    () => cloudEdgePortAssignments(layoutInput.edges, positionsByNodeId, cloudNodesById, layoutInput.externalAttachmentIds),
    [cloudNodesById, layoutInput.edges, layoutInput.externalAttachmentIds, positionsByNodeId]
  );
  const edges = useMemo((): Edge[] => layoutInput.edges.map((edge) => {
    const appearance = cloudEdgeAppearance(edge.role);
    const handles = edgePortsById.get(edge.id) ?? cloudRelationshipHandles(edge, cloudNodesById, layoutInput.externalAttachmentIds, positionsByNodeId);
    return {
      id: edge.id,
      source: edge.fromId,
      target: edge.toId,
      // A structural relation has fixed centre anchors. Drawing it straight
      // makes the source/destination clear; a Bézier path becomes nearly
      // horizontal across wide layers and appears to bend into the card.
      type: usesCentralVerticalPorts(edge) ? 'straight' : 'default',
      sourceHandle: handles.sourceHandle,
      targetHandle: handles.targetHandle,
      selected: selectedEdgeIds.has(edge.id),
      animated: false,
      interactionWidth: 24,
      zIndex: 4,
      markerEnd: { type: MarkerType.ArrowClosed, color: appearance.color, width: 18, height: 18 },
      style: {
        stroke: appearance.color,
        strokeDasharray: appearance.dash,
        strokeWidth: appearance.width,
        strokeLinecap: 'butt',
        strokeLinejoin: 'miter'
      },
      data: { role: edge.role, relation: edge.relation }
    };
  }), [cloudNodesById, edgePortsById, layoutInput.edges, layoutInput.externalAttachmentIds, positionsByNodeId, selectedEdgeIds]);
  useEffect(() => {
    if (!instance || nodes.length === 0) return;
    const frame = requestAnimationFrame(() => instance.fitView({ padding: 0.12, minZoom: 0.55, maxZoom: 0.8, duration: 0 }));
    return () => cancelAnimationFrame(frame);
  }, [instance, nodes.length, graph.graphId]);
  if (layoutInput.nodes.length === 0) return <div className="topology-diagram__canvas topology-canvas cloud-connectivity__empty">No cloud connectivity resources are available for this scope.</div>;
  return <div className="topology-diagram__canvas topology-canvas cloud-connectivity__surface">
    <ReactFlow
      nodes={nodes} edges={edges} nodeTypes={{ cloud: CloudNodeCard }} onInit={setInstance}
      nodesDraggable nodesConnectable={false} minZoom={0.2} maxZoom={2.5} zoomOnDoubleClick={false} panOnScroll zoomOnScroll={false}
      onNodeClick={(event, node) => onNodeSelect(event as unknown as ReactMouseEvent, node.id)}
      onEdgeClick={(event, edge) => onEdgeSelect(event as unknown as ReactMouseEvent, edge.id)}
      onPaneClick={(event) => onPaneClick(event as unknown as ReactMouseEvent)} proOptions={{ hideAttribution: true }}
    >
      <Background gap={22} size={1} color="rgba(56, 96, 110, 0.16)" />
      <MiniMap pannable zoomable />
      <Controls />
    </ReactFlow>
  </div>;
};

export default CloudConnectivityView;
